import React, { useEffect, useState, useCallback } from "react"
import { Row, Col, Spinner, Alert, Card, Badge } from "react-bootstrap"
import FsLightbox from "fslightbox-react"
import API from "../../common/API"
import Settings from "../../common/Settings"
import { useTranslation } from "react-i18next"
import "./modernGallery.css"

/**
 * ModernVisitGallery - Modern photo gallery component with lightbox
 * Fixed to use exact same API logic as original VisitGallery
 */
const ModernVisitGallery = ({ id }) => {
  const { t } = useTranslation("global")
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1,
  })

  // Use the exact same logic as the original VisitGallery component
  const bindData = useCallback(async () => {
    if (!id) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      let query = "id=" + id
      let requestAPI = await API.getAction("place/ListImages", query)

      if (requestAPI.data.status === "ok") {
        console.log("API Response (original logic):", requestAPI)

        // Use the exact same mapping as the original component
        let response = requestAPI.data.response.map(item => {
          item.loading = "lazy"
          item.thumbnailHeight = "100"
          item.thumbnailWidth = "100"
          return item
        })

        console.log("Mapped images:", response)
        setImages(response)
      } else {
        throw new Error(requestAPI.data.message || t("Failed to load images"))
      }
    } catch (err) {
      console.error("Error fetching images:", err)
      setError(err.message || t("Error loading photos"))
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    bindData()
  }, [bindData])

  // Open lightbox at specific image
  const openLightbox = useCallback(
    index => {
      setLightboxController({
        toggler: !lightboxController.toggler,
        slide: index + 1,
      })
    },
    [lightboxController.toggler]
  )

  // Retry loading images
  const handleRetry = () => {
    bindData()
  }

  // Loading state
  if (loading) {
    return (
      <div className="modern-gallery-loading">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">{t("Loading photos...")}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="modern-gallery-error">
        <Alert variant="warning" className="text-center">
          <i
            className="fa fa-exclamation-triangle mb-2"
            style={{ fontSize: "2rem" }}
          ></i>
          <p className="mb-2">{error}</p>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleRetry}
          >
            <i className="fa fa-refresh me-2"></i>
            {t("Try again")}
          </button>
        </Alert>
      </div>
    )
  }

  // No images state
  if (!images || images.length === 0) {
    return (
      <div className="modern-gallery-empty">
        <div className="text-center py-5">
          <i
            className="fa fa-images text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <p className="mt-3 text-muted">{t("No photos available")}</p>
        </div>
      </div>
    )
  }

  // Prepare lightbox sources - use getThumbnail instead of getFile
  const lightboxSources = images.map(
    img => `${img.image || img.original || img.thumbnail}`
  )

  return (
    <div className="modern-gallery">
      {/* Gallery Header */}
      <div className="modern-gallery-header mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 d-flex align-items-center">
            <i className="fa fa-images me-2 text-primary"></i>
            {t("Photo Gallery")}
          </h6>
          <Badge bg="secondary" className="photo-count">
            {images.length} {images.length === 1 ? t("photo") : t("photos")}
          </Badge>
        </div>
      </div>

      {/* Photos Grid */}
      <Row className="modern-gallery-grid g-2">
        {images.map((image, index) => (
          <Col key={image.id || index} xs={6} sm={4} md={3} lg={2}>
            <Card
              className="modern-gallery-item h-100"
              onClick={() => openLightbox(index)}
              style={{ cursor: "pointer" }}
            >
              <div className="modern-gallery-image-container">
                <img
                  src={`${image.image || image.thumbnail || image.original}`}
                  alt={image.originalAlt || `Photo ${index + 1}`}
                  className="modern-gallery-image"
                  loading="lazy"
                  onLoad={() =>
                    console.log(
                      "✅ Image loaded successfully:",
                      image.image || image.thumbnail || image.original
                    )
                  }
                  onError={e => {
                    console.error("❌ Image failed to load:", {
                      src: e.target.src,
                      image: image.image,
                      thumbnail: image.thumbnail,
                      original: image.original,
                      fullItem: image,
                    })
                    e.currentTarget.src =
                      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIGltYWdlbjwvdGV4dD48L3N2Zz4="
                  }}
                />

                {/* Hover overlay */}
                <div className="modern-gallery-overlay">
                  <div className="modern-gallery-overlay-content">
                    <i className="fa fa-search-plus"></i>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Lightbox */}
      <FsLightbox
        toggler={lightboxController.toggler}
        sources={lightboxSources}
        slide={lightboxController.slide}
        type="image"
        maxYoutubeVideoDimensions={{ width: 900, height: 600 }}
      />
    </div>
  )
}

export default ModernVisitGallery
